import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from "react-router-dom";

const FeaturedWork = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  const projects = [
    {
      couple: 'Chris & Medha',
      location: 'Tuscany, Italy',
      year: '2024',
      image: 'assets/chrismedha.webp',
      category: 'Destination Wedding',
      slug: 'wedding/chris-medha',
    },
    {
      couple: 'Vyshnavie & Anirudh',
      location: 'Udaipur Palace',
      year: '2025',
      image: 'assets/vsa_cover_photo.webp',
      category: 'Royal Wedding',
      slug: 'wedding/vyshanavie-anirudh',
    },
    {
      couple: 'Benny & Vaishnavi',
      location: 'Udaipur Palace',
      year: '2024',
      image: 'assets/wedding/Benny_Vaishnavi_webp/w15.webp',
      category: 'Intimate Ceremony',
      slug: 'wedding/benny-vaishnavi',
    },
    {
      couple: 'Prathyusha & Pavan',
      location: 'Hyderabad',
      year: '2024',
      image: 'https://ik.imagekit.io/p3xaqnkci/tagteam/prathyusha_pavan_cover_photo.webp',
      category: 'Royal Wedding',
      slug: 'wedding/prathyusha-pavan',
    },
  ];


  const nextSlide = () =>
    setActiveIndex((prev) => (prev + 1) % projects.length);

  const prevSlide = () =>
    setActiveIndex((prev) => (prev - 1 + projects.length) % projects.length);

  // ✅ Auto-slide every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % projects.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [projects.length]);

  return (
    <section id="portfolio" className="bg-neutral-950 py-8 lg:py-12">
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-8 lg:mb-10">
          <div>
            <span className="text-[#d4af37] text-xs tracking-[0.4em] block mb-4">
              FEATURED WORK
            </span>
            <h2 className="text-4xl lg:text-6xl text-white font-serif font-light">
              Stories We've
              <span className="italic text-neutral-400"> Captured</span>
            </h2>
          </div>

          <div className="flex gap-4 mt-8 lg:mt-0">
            <button
              onClick={prevSlide}
              className="cursor-hover w-14 h-14 border border-neutral-700 flex items-center justify-center hover:border-[#d4af37] hover:text-[#d4af37] transition-colors duration-300 text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <button
              onClick={nextSlide}
              className="cursor-hover w-14 h-14 border border-neutral-700 flex items-center justify-center hover:border-[#d4af37] hover:text-[#d4af37] transition-colors duration-300 text-white"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {projects.map((project, i) => (
              <div key={i} className="min-w-full px-0 lg:px-4">
                <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-5 lg:gap-8 items-center">

                  {/* Image (Smaller Size Applied) */}
                  <div className="relative aspect-[16/10] w-full max-w-[800px] group cursor-hover lg:ml-24">
                    <img
                      src={project.image}
                      alt={project.couple}
                      className="absolute inset-0 w-full h-full object-contain transition-transform duration-1000 group-hover:scale-105"
                    />

                  <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/60 via-transparent to-transparent" />

                  <div className="absolute inset-0 bg-[#d4af37]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Corner Accents */}
                  <div className="absolute top-6 left-6 w-12 h-12 border-l border-t border-white/30" />
                  <div className="absolute bottom-6 right-6 w-12 h-12 border-r border-b border-white/30" />
                </div>

                {/* Info */}
                <div className="lg:pl-8">
                  <span className="text-[#d4af37] text-xs tracking-[0.3em]">
                    {project.category}
                  </span>

                  <h3 className="text-3xl lg:text-5xl text-white font-serif font-light mt-4 leading-tight">
                    {project.couple}
                  </h3>

                  <div className="flex items-center gap-4 mt-4 text-neutral-400">
                    {/* <MapPin className="w-4 h-4" />
                      <span className="tracking-wider">{project.location}</span> */}
                    <span className="text-neutral-600">|</span>
                    <span>{project.year}</span>
                  </div>

                  <p className="text-neutral-500 mt-4 leading-relaxed max-w-md">
                    A breathtaking celebration of love, captured in every tender moment and grand gesture.
                  </p>

                  <button onClick={() => navigate(`/gallery/${project.slug}`)} className="cursor-hover mt-6 text-white tracking-[0.2em] text-sm flex items-center gap-4 group">
                    <span className="border-b border-transparent group-hover:border-[#d4af37] pb-1 transition-colors duration-300">
                      VIEW GALLERY
                    </span>
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
                  </button>
                </div>
              </div>
              </div>
            ))}
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-3 mt-6">
          {projects.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`cursor-hover h-0.5 transition-all duration-500 ${i === activeIndex
                ? 'w-12 bg-[#d4af37]'
                : 'w-6 bg-neutral-700 hover:bg-neutral-500'
                }`}
            />
          ))}
        </div>
      </div>
    </div>
    </section >
  );
};
export default FeaturedWork;